import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  Shield,
  Scale,
  ArrowRight,
  CheckCircle,
  Building2,
  Gavel,
  Megaphone,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Heart,
  Briefcase,
  Clock,
  XCircle,
  AlertTriangle,
  FileText,
  Landmark,
  HandMetal,
  Ban,
  Eye,
  Play,
} from "lucide-react";

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-bold text-slate-900 pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#002B7F] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const sectionNav = [
  { id: "what-is-union", label: "What Is a Union" },
  { id: "benefits", label: "Benefits" },
  { id: "whats-broken", label: "What's Broken" },
  { id: "right-to-work", label: "Right-to-Work" },
  { id: "worker-agency", label: "Worker Power Agency" },
  { id: "union-formation", label: "Union Formation" },
  { id: "first-contract", label: "First Contract" },
  { id: "union-busting", label: "Union Busting" },
  { id: "anti-union-meetings", label: "Anti-Union Meetings" },
  { id: "state-money", label: "State Money" },
];

const unionBenefits = [
  {
    icon: DollarSign,
    title: "Higher Wages",
    desc: "Union workers earn 10–20% more on average than non-union workers doing the same job",
    accent: "bg-emerald-50 border-emerald-200 text-emerald-800",
    iconColor: "text-emerald-600",
  },
  {
    icon: Heart,
    title: "Better Healthcare",
    desc: "Far more likely to have healthcare coverage that actually protects you and your family",
    accent: "bg-blue-50 border-blue-200 text-blue-800",
    iconColor: "text-blue-600",
  },
  {
    icon: Shield,
    title: "Retirement Security",
    desc: "Access to retirement plans you can actually rely on — not just empty promises",
    accent: "bg-violet-50 border-violet-200 text-violet-800",
    iconColor: "text-violet-600",
  },
  {
    icon: Briefcase,
    title: "Safer Conditions",
    desc: "Stronger workplace safety standards negotiated and enforced by workers themselves",
    accent: "bg-amber-50 border-amber-200 text-amber-800",
    iconColor: "text-amber-600",
  },
  {
    icon: Scale,
    title: "Protection from Unfair Firing",
    desc: "Real due process before termination — not at-will dismissal with no recourse",
    accent: "bg-rose-50 border-rose-200 text-rose-800",
    iconColor: "text-rose-600",
  },
];

const brokenItems = [
  {
    icon: Clock,
    title: "Employers Can Delay Union Formation",
    desc: "Companies drag out the process for months or years, hoping workers give up",
  },
  {
    icon: Megaphone,
    title: "Mandatory Anti-Union Meetings",
    desc: "Workers are forced to attend one-sided propaganda sessions just to keep their jobs",
  },
  {
    icon: AlertTriangle,
    title: "Pressure and Intimidation",
    desc: "Workers face retaliation, threats, and fear campaigns when they try to organize",
  },
  {
    icon: XCircle,
    title: "Contract Negotiations Can Stall for Years",
    desc: "Even after winning a vote, companies can block real contracts indefinitely",
  },
];

const enforcementTools = [
  {
    icon: Scale,
    title: "Triple Damages",
    desc: "Workers can sue for triple damages in retaliation lawsuits — making violations extremely costly",
  },
  {
    icon: DollarSign,
    title: "Massive State Fines",
    desc: "Significant fines imposed per violation — not token penalties that companies can absorb",
  },
  {
    icon: Gavel,
    title: "Personal Executive Liability",
    desc: "Executives who knowingly violate worker rights may face personal liability and criminal penalties",
  },
  {
    icon: Landmark,
    title: "Fast-Track Worker Lawsuits",
    desc: "Workers can sue immediately in state courts — no waiting, no bureaucratic delays",
  },
];

const faqs = [
  {
    question: "Is this legal?",
    answer:
      "Yes. States have broad authority to set labor standards, protect workers, and establish agencies to enforce worker rights. Every element of this plan operates within existing state authority. The Worker Power Agency, the Worker Justice Board, and the enforcement mechanisms are all designed to be legally durable.",
  },
  {
    question: "Does this hurt businesses?",
    answer:
      "No. Strong workers and strong businesses go together. When workers earn fair wages, they spend money locally. When workers have healthcare, businesses have healthier, more productive employees. When workers feel respected, turnover drops and productivity rises. The businesses that thrive are the ones that treat their workers fairly.",
  },
  {
    question: "What if I'm not in a union?",
    answer:
      "Even if you are not personally in a union, strong unions raise standards for all workers in a region. When union wages go up, non-union employers must compete — meaning your wages are more likely to rise too. Union standards create a floor that lifts everyone.",
  },
  {
    question: "What about union dues?",
    answer:
      "Even after paying union dues, union workers still take home more money on average than non-union workers doing the same job. Dues fund the negotiation, legal protection, and representation that deliver higher wages, better benefits, and job security. It is an investment that pays for itself.",
  },
  {
    question: "What is the Worker Justice Board?",
    answer:
      "The Nevada Worker Justice Board is a public, transparent panel made up of labor experts, neutral decision-makers, and advocates for fairness. If a company and a newly formed union cannot reach a contract within a set timeline, the Board reviews both sides and issues a final, binding contract covering wages, benefits, and working conditions.",
  },
  {
    question: "Can my employer punish me for supporting a union?",
    answer:
      "Under this plan, retaliation against workers for supporting a union will carry severe consequences — including triple damages, massive fines, personal executive liability, and fast-track state lawsuits. Retaliating against workers will not be a cost of doing business in Nevada.",
  },
];

const ProUnionPage = () => {
  const [showNav, setShowNav] = useState(false);

  return (
    <div data-testid="pro-union-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-24 bg-slate-900">
        <div className="absolute inset-0 bg-[#002B7F]/90" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            Nevada Worker Power Plan
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4 max-w-4xl">
            If You Work Hard in Nevada, You Should Be Able to Live Well in
            Nevada.
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-8">
            A plan to raise wages, protect workers, and make Nevada the most
            pro-union state in America.
          </p>

          <div className="flex flex-wrap gap-4">
            <a
              href="#what-is-union"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              See the Worker Power Plan
            </a>
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Get Involved
            </Link>
          </div>
        </div>
      </section>

      {/* ── SECTION NAV (sticky on mobile) ── */}
      <div className="sticky top-16 md:top-20 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile toggle */}
          <button
            onClick={() => setShowNav(!showNav)}
            className="lg:hidden w-full flex items-center justify-between py-3 text-sm font-bold text-[#002B7F]"
          >
            Jump to Section
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showNav ? "rotate-180" : ""}`}
            />
          </button>
          {/* Desktop + expanded mobile */}
          <nav
            className={`${showNav ? "block" : "hidden"} lg:block overflow-x-auto`}
          >
            <div className="flex items-center gap-1 py-2 min-w-max">
              {sectionNav.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={() => setShowNav(false)}
                  className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-[#002B7F] hover:bg-slate-50 transition-colors whitespace-nowrap"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </nav>
        </div>
      </div>

      {/* ── SECTION 1: WHAT IS A UNION ── */}
      <section id="what-is-union" className="py-16 md:py-24 bg-white scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            What a Union Really Is
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              {
                icon: Users,
                title: "Workers Acting Together",
                desc: "A union is simply a group of workers who come together to negotiate as one — so they don't have to face the company alone.",
              },
              {
                icon: HandMetal,
                title: "Collective Strength",
                desc: "Instead of negotiating individually — where the company holds all the power — workers negotiate as a group, balancing the scales.",
              },
              {
                icon: Scale,
                title: "Fairness at Work",
                desc: "Unions ensure that the people who do the work have a real voice in determining their wages, benefits, and conditions.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-[#F8F9FA] border border-slate-200 p-6"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#002B7F] text-white mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6 text-center">
            <p className="text-[#002B7F] font-bold text-lg">
              "A union is not about politics. It is about fairness at work."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 2: BENEFITS OF UNION JOBS ── */}
      <section id="benefits" className="py-16 md:py-24 bg-[#F8F9FA] scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 text-center">
            Why Union Jobs Change Lives
          </h2>
          <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
            The data is clear: union membership leads to higher pay, better
            benefits, and stronger protections for working families.
          </p>

          <div className="space-y-4 mb-8">
            {unionBenefits.map((benefit) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={benefit.title}
                  className={`flex items-start gap-4 p-5 border ${benefit.accent}`}
                >
                  <Icon
                    className={`w-6 h-6 ${benefit.iconColor} flex-shrink-0 mt-0.5`}
                  />
                  <div>
                    <p className="font-bold text-sm mb-1">{benefit.title}</p>
                    <p className="text-sm opacity-80">{benefit.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-slate-900 p-6 text-center">
            <p className="text-white font-bold">
              "Even after union dues, union workers still take home more money
              on average."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 3: WHAT'S BROKEN TODAY ── */}
      <section id="whats-broken" className="py-16 md:py-24 bg-white scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Why the System Isn't Fair Right Now
          </h2>
          <p className="text-slate-600 mb-8 max-w-3xl">
            If unions are so beneficial, why doesn't everyone have one? Because
            the current system is designed to make it difficult.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {brokenItems.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="flex items-start gap-3 p-5 bg-red-50 border border-red-200"
                >
                  <Icon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-red-800 text-sm">
                      {item.title}
                    </p>
                    <p className="text-red-700 text-xs mt-1">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-slate-900 p-6 text-center">
            <p className="text-white font-bold">
              "Winning a union vote is not enough if companies can still block
              the results."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: END RIGHT-TO-WORK ── */}
      <section id="right-to-work" className="py-16 md:py-24 bg-[#F8F9FA] scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            Ending Right-to-Work Laws
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <p className="text-slate-600 leading-relaxed mb-4">
                Many people hear "right-to-work" and assume it means the right
                to a job. That is misleading.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                In reality, right-to-work laws allow workers to benefit from
                union-negotiated wages, benefits, and protections — without
                contributing to the cost of that representation.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Over time, this weakens unions, lowers collective bargaining
                power, and reduces wages and protections for everyone — union
                and non-union alike.
              </p>
            </div>

            <div>
              <div className="bg-red-50 border border-red-200 p-6 mb-4">
                <h3 className="font-bold text-red-800 text-sm mb-3">
                  What Right-to-Work Actually Does
                </h3>
                <div className="space-y-2">
                  {[
                    "Allows free-riding on union contracts",
                    "Drains union resources over time",
                    "Weakens bargaining power for all workers",
                    "Lowers wages across entire industries",
                  ].map((item, i) => (
                    <div key={item} className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <span className="text-red-700 text-xs">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 p-6">
                <h3 className="font-bold text-emerald-800 text-sm mb-3">
                  The Fair Alternative
                </h3>
                <div className="space-y-2">
                  {[
                    "Everyone who benefits contributes fairly",
                    "Unions stay strong and well-funded",
                    "Better contracts for all workers",
                    "Higher wages across the board",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span className="text-emerald-700 text-xs">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6 text-center">
            <p className="text-[#002B7F] font-bold">
              "If a union negotiates better wages and benefits for you,
              contributing your fair share is simply fairness."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 5: WORKER POWER AGENCY ── */}
      <section id="worker-agency" className="py-16 md:py-24 bg-[#002B7F] scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Landmark className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Nevada Worker Power Agency
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              A new state agency — fully accountable to the public — that exists
              solely to empower workers and enforce fairness.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {[
              {
                icon: Users,
                title: "Helps Workers Form Unions",
                desc: "Direct assistance and guidance through every step of the organizing process",
              },
              {
                icon: FileText,
                title: "Explains Worker Rights",
                desc: "Clear, accessible education about rights, protections, and options",
              },
              {
                icon: Shield,
                title: "Legal Support Against Retaliation",
                desc: "Free legal resources for workers facing employer intimidation or retaliation",
              },
              {
                icon: Eye,
                title: "Tracks Employer Violations",
                desc: "A public record of companies that violate worker rights",
              },
              {
                icon: FileText,
                title: "Public Transparency Reports",
                desc: "Regular published reports on the state of worker rights in Nevada",
              },
              {
                icon: Gavel,
                title: "Enforcement Authority",
                desc: "Real power to investigate complaints and hold violators accountable",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white/10 border border-white/20 p-5"
                >
                  <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                  <h3 className="font-bold text-white text-sm mb-1">
                    {item.title}
                  </h3>
                  <p className="text-white/70 text-xs leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="bg-white/10 border border-white/20 p-5 text-center">
            <p className="text-[#D4AF37] font-bold">
              "Workers should not have to fight alone against billion-dollar
              companies."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: MAKING UNION FORMATION SIMPLE ── */}
      <section id="union-formation" className="py-16 md:py-24 bg-white scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            Making Union Formation Fair and Fast
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <p className="text-slate-600 leading-relaxed mb-4">
                Wherever the law allows, Nevada will implement a clear, simple
                process: if a majority of workers sign union authorization
                cards, that union will be recognized.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                No drawn-out delays. No intimidation campaigns. No company-run
                anti-union propaganda. A majority decision should be final.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Democracy at work should look like democracy — not a
                corporate-controlled process designed to exhaust workers into
                giving up.
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  num: "1",
                  title: "Workers sign authorization cards",
                  desc: "A simple, secure way to indicate support for union representation",
                },
                {
                  num: "2",
                  title: "Majority verified",
                  desc: "If a majority of workers sign, the decision is clear",
                },
                {
                  num: "3",
                  title: "Union recognized immediately",
                  desc: "No unnecessary delays, hearings, or employer-driven stalling",
                },
                {
                  num: "4",
                  title: "Contract negotiations begin",
                  desc: "Workers move directly to negotiating wages, benefits, and conditions",
                },
              ].map((step) => (
                <div
                  key={step.num}
                  className="flex items-start gap-3 p-4 bg-[#F8F9FA] border border-slate-200"
                >
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white font-bold text-sm">
                    {step.num}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">
                      {step.title}
                    </p>
                    <p className="text-slate-600 text-xs">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6 text-center">
            <p className="text-[#002B7F] font-bold">
              "If most workers agree, that decision should be respected
              immediately."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 7: GUARANTEED FIRST CONTRACT ── */}
      <section id="first-contract" className="py-16 md:py-24 bg-[#F8F9FA] scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Guaranteeing a Real Contract
          </h2>
          <p className="text-slate-600 mb-8 max-w-3xl">
            Winning a union is only the first step. Right now, companies can
            stall contract negotiations for months — or even years — leaving
            workers with nothing. That ends under this plan.
          </p>

          <div className="bg-white border border-slate-200 p-6 mb-8">
            <div className="flex items-start gap-3 mb-6">
              <Clock className="w-6 h-6 text-[#002B7F] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-slate-900 mb-2">
                  Strict Negotiation Timeline
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Once a union is formed, there will be a strict timeline to
                  reach a contract. Companies can no longer run out the clock.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center my-6">
              <ArrowRight className="w-6 h-6 text-slate-400" />
            </div>

            <div className="bg-[#002B7F] p-6 text-white">
              <div className="flex items-start gap-4">
                <Landmark className="w-8 h-8 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold mb-2">
                    Nevada Worker Justice Board
                  </h3>
                  <p className="text-white/80 text-sm leading-relaxed mb-4">
                    If no agreement is reached within the timeline, the case
                    goes to the Nevada Worker Justice Board — a public,
                    transparent panel made up of labor experts, neutral
                    decision-makers, and advocates for fairness.
                  </p>
                  <div className="space-y-2">
                    {[
                      "Reviews both sides thoroughly",
                      "Evaluates wages, benefits, and conditions",
                      "Issues a final, binding contract",
                      "Fully transparent and publicly accountable",
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                        <span className="text-white/90 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-900 p-6 text-center">
            <p className="text-white font-bold">
              "No more endless delays. No more stalled negotiations. Workers
              will get a real contract."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 8: PUNISHING UNION BUSTING ── */}
      <section id="union-busting" className="py-16 md:py-24 bg-white scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Holding Companies Accountable
          </h2>
          <p className="text-slate-600 mb-8 max-w-3xl">
            If a company retaliates against a worker for supporting a union,
            there will be serious consequences.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {enforcementTools.map((tool) => {
              const Icon = tool.icon;
              return (
                <div
                  key={tool.title}
                  className="bg-[#F8F9FA] border border-slate-200 p-5"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-sm mb-1">
                        {tool.title}
                      </h3>
                      <p className="text-slate-600 text-xs leading-relaxed">
                        {tool.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-slate-900 p-6 text-center">
            <p className="text-white font-bold">
              "Retaliating against workers will not be a cost of doing business
              in Nevada."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 9: ENDING FORCED ANTI-UNION MEETINGS ── */}
      <section id="anti-union-meetings" className="py-16 md:py-24 bg-[#F8F9FA] scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            Fair Information in the Workplace
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-red-50 border border-red-200 p-6">
              <Ban className="w-8 h-8 text-red-600 mb-3" />
              <h3 className="font-bold text-red-800 mb-3">
                Ban Mandatory Anti-Union Meetings
              </h3>
              <p className="text-red-700 text-sm leading-relaxed">
                No worker should be forced to sit through hours of one-sided
                propaganda just to keep their job. Mandatory "captive audience"
                meetings where companies pressure workers against unionizing
                will be prohibited.
              </p>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 p-6">
              <Scale className="w-8 h-8 text-emerald-600 mb-3" />
              <h3 className="font-bold text-emerald-800 mb-3">
                Equal Time for Union Representatives
              </h3>
              <p className="text-emerald-700 text-sm leading-relaxed">
                If companies do hold informational meetings, unions will be
                given equal, paid time to present their side. Workers deserve to
                hear both perspectives — not just the company's version of the
                story.
              </p>
            </div>
          </div>

          <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6 text-center">
            <p className="text-[#002B7F] font-bold">
              "Workers deserve both sides — not just company messaging."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 10: STATE MONEY ONLY FOR FAIR EMPLOYERS ── */}
      <section id="state-money" className="py-16 md:py-24 bg-[#002B7F] scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Building2 className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Protecting Taxpayer Dollars
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Any company receiving state contracts, tax incentives, or public
              funding must respect workers' rights. No exceptions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              {
                title: "Remain Neutral",
                desc: "Stay neutral during union organizing — no interference, no intimidation",
              },
              {
                title: "Respect Worker Decisions",
                desc: "Honor the results of union votes and card-check processes",
              },
              {
                title: "Bargain in Good Faith",
                desc: "Negotiate contracts honestly and within required timelines",
              },
            ].map((item, i) => (
              <div key={item.title} className="bg-white/10 border border-white/20 p-5">
                <CheckCircle className="w-6 h-6 text-[#D4AF37] mb-3" />
                <h3 className="font-bold text-white text-sm mb-2">
                  {item.title}
                </h3>
                <p className="text-white/70 text-xs leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-white/10 border border-white/20 p-6 mb-6">
            <h3 className="font-bold text-white mb-4">
              Penalties for Violation
            </h3>
            <div className="space-y-3">
              {[
                "Loss of contracts or funding immediately",
                "Ban from future state business",
                "Public listing of violations for full transparency",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <span className="text-white/90 text-sm font-medium">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/10 border border-white/20 p-5 text-center">
            <p className="text-[#D4AF37] font-bold">
              "Taxpayer money will never fund union-busting. Not now, not ever."
            </p>
          </div>
        </div>
      </section>

      {/* ── WHY THIS MATTERS ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">
            A Stronger Nevada Starts with Stronger Workers
          </h2>

          <div className="grid sm:grid-cols-2 gap-4 text-left mb-10">
            {[
              "When workers have power, wages rise",
              "Families stabilize and communities grow",
              "Local economies become stronger",
              "Businesses attract better talent",
              "Workplace safety improves for everyone",
              "Democracy in the workplace strengthens democracy everywhere",
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 bg-[#F8F9FA] border border-slate-200 p-4"
              >
                <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <span className="text-slate-800 font-medium text-sm">
                  {item}
                </span>
              </div>
            ))}
          </div>

          <div className="bg-[#002B7F] p-8">
            <p className="text-white font-bold text-lg leading-relaxed">
              "If you work hard in Nevada, you deserve more than survival. You
              deserve security, dignity, and a fair shot at the American Dream."
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Stand with Nevada Workers
          </h2>
          <p className="text-white/70 mb-8">
            Every worker deserves a voice, fair pay, and real protection. Join
            the movement to make Nevada the strongest state for workers in
            America.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Get Involved
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
            <Link
              to="/policies/pro-worker"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              Pro-Worker Policy <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProUnionPage;
